import { useEffect, useState } from "react";

export type FieldGpsStatus = "capturado" | "indisponivel" | "buscando" | "baixa_precisao";

export interface FieldGpsState {
  status: FieldGpsStatus;
  latitude: number | null;
  longitude: number | null;
  /** Precisao informada pelo aparelho, em metros. */
  accuracy: number | null;
  error: string | null;
}

/** Acima disso a posicao serve para orientar, nao para cadastrar. */
const LOW_ACCURACY_M = 15;

/** Posicao real do aparelho (navigator.geolocation). Nada e simulado. */
export function useFieldGps(): FieldGpsState {
  const [state, setState] = useState<FieldGpsState>({
    status: "buscando",
    latitude: null,
    longitude: null,
    accuracy: null,
    error: null,
  });

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setState((s) => ({ ...s, status: "indisponivel", error: "Este navegador não oferece geolocalização." }));
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const accuracy = pos.coords.accuracy;
        setState({
          status: accuracy > LOW_ACCURACY_M ? "baixa_precisao" : "capturado",
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy,
          error: null,
        });
      },
      (err) => {
        setState((s) => ({
          ...s,
          status: "indisponivel",
          error: err.code === err.PERMISSION_DENIED ? "Permissão de localização negada." : err.message,
        }));
      },
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 30_000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  return state;
}

/** Estado real da conexao do navegador. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}
